import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { FormField, FormInput, FormSelect } from '@practium/ui';
import type { PropertyFormData, PropertyPageMode } from '../../../types';
import { STATE_OPTIONS } from '../../../constants/properties.constants';
import { applyCepMask, applySubscriptionMask } from '../../../utils/masks';
import { usePropertyForm } from '../../../hooks/usePropertyForm';
import styles from '../PropertyForm.module.css';

export interface PropertyFormRef {
	submit: () => Promise<void>;
	isDirty: () => boolean;
}

interface PropertyFormProps {
	mode: PropertyPageMode;
	initialData?: PropertyFormData;
	onSubmit: (createdId?: string) => void;
	onLoadingChange: (loading: boolean) => void;
	avatarfile?: File | null;
}

export const PropertyForm = forwardRef<PropertyFormRef, PropertyFormProps>(
	function PropertyForm({ mode, initialData, onSubmit, onLoadingChange, avatarfile}, ref) {
		const {
			form,
			errors,
			isLoading,
			isStateEditable,
			autoFilledFields,
			set,
			handleCepBlur,
			handleSubmit,
			isDirty,
		} = usePropertyForm({ initialData, onSubmit, avatarfile });

		useImperativeHandle(ref, () => ({ submit: handleSubmit, isDirty }));

		useEffect(() => {
			onLoadingChange(isLoading);
		}, [isLoading, onLoadingChange]);

		const isView = mode === 'view';

		if (isView) {
			return (
				<div className={styles.viewGrid}>
					<div className={styles.viewField}>
						<span className={styles.viewLabel}>Nome</span>
						<span className={styles.viewValue}>{form.name || '—'}</span>
					</div>
					<div className={styles.viewField}>
						<span className={styles.viewLabel}>Inscrição</span>
						<span className={styles.viewValue}>{form.subscription || '—'}</span>
					</div>
					<div className={styles.viewField}>
						<span className={styles.viewLabel}>CEP</span>
						<span className={styles.viewValue}>{form.cep || '—'}</span>
					</div>
					<div className={styles.viewField}>
						<span className={styles.viewLabel}>Estado</span>
						<span className={styles.viewValue}>{form.state || '—'}</span>
					</div>
					<div className={styles.viewField}>
						<span className={styles.viewLabel}>Cidade</span>
						<span className={styles.viewValue}>{form.city || '—'}</span>
					</div>
					<div className={styles.viewField}>
						<span className={styles.viewLabel}>Logradouro</span>
						<span className={styles.viewValue}>{form.street || '—'}</span>
					</div>
					<div className={styles.viewField}>
						<span className={styles.viewLabel}>Bairro</span>
						<span className={styles.viewValue}>{form.district || '—'}</span>
					</div>
					<div className={styles.viewField}>
						<span className={styles.viewLabel}>Número</span>
						<span className={styles.viewValue}>{form.number || '—'}</span>
					</div>
				</div>
			);
		}

		return (
			<div className={styles.formGrid}>
				<FormField label="Nome" required error={errors.name}>
					<FormInput
						value={form.name}
						onChange={e => set('name', e.target.value)}
						placeholder="Ex.: Fazenda Esperança"
						hasError={!!errors.name}
					/>
				</FormField>
				<FormField label="Inscrição" error={errors.subscription}>
					<FormInput
						value={form.subscription}
						onChange={e => set('subscription', applySubscriptionMask(e.target.value))}
						placeholder="Ex.: xx.xxx.xxx-x"
						hasError={!!errors.subscription}
					/>
				</FormField>
				<FormField label="CEP" required error={errors.cep}>
					<FormInput
						value={form.cep}
						onChange={e => set('cep', applyCepMask(e.target.value))}
						onBlur={() => handleCepBlur(form.cep)}
						placeholder="Ex.: 12.345-678"
						hasError={!!errors.cep}
					/>
				</FormField>
				<FormField label="Estado" required error={errors.state}>
					<FormSelect
						value={form.state}
						onChange={v => set('state', v)}
						options={STATE_OPTIONS}
						placeholder="Selecione o Estado"
						hasError={!!errors.state}
						searchable
						disabled={!isStateEditable}
					/>
				</FormField>
				<FormField label="Cidade" error={errors.city}>
					<FormInput
						value={form.city}
						onChange={e => set('city', e.target.value)}
						placeholder="Ex.: Goiânia"
						hasError={!!errors.city}
						className={autoFilledFields.has('city') ? styles.autoFilled : undefined}
					/>
				</FormField>
				<FormField label="Logradouro" error={errors.street}>
					<FormInput
						value={form.street}
						onChange={e => set('street', e.target.value)}
						placeholder="Ex.: Rodovia BR-153"
						hasError={!!errors.street}
						className={autoFilledFields.has('street') ? styles.autoFilled : undefined}
					/>
				</FormField>
				<FormField label="Bairro" error={errors.district}>
					<FormInput
						value={form.district}
						onChange={e => set('district', e.target.value)}
						placeholder="Ex.: Zona Rural"
						hasError={!!errors.district}
						className={autoFilledFields.has('district') ? styles.autoFilled : undefined}
					/>
				</FormField>
				<FormField label="Número" error={errors.number}>
					<FormInput
						value={form.number}
						onChange={e => set('number', e.target.value)}
						placeholder="Ex.: 100"
						hasError={!!errors.number}
					/>
				</FormField>
			</div>
		);
	},
);
